import { useEffect, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { LOAI_DON_VI, NHAN_LOAI_DON_VI } from '../constants';
import { LoiForm } from './LoiForm';
import type { DonVi, GhiDonVi, LoaiDonVi } from '../types';

interface FormState {
  name: string;
  type: LoaiDonVi;
  score: string;
  supervisor: string;
  parentId: string;
  functions: string;
}

const RONG: FormState = { name: '', type: 'CO_QUAN', score: '8.0', supervisor: '', parentId: '', functions: '' };

type Loi = Partial<Record<'name' | 'score' | 'parent_id' | 'functions', string>>;

/**
 * Thêm / sửa cơ quan, phòng ban, cá nhân (form của `VaiTroCacCapView` bản cũ). Cá nhân không có người phụ
 * trách; "Trực thuộc" chỉ chọn cơ quan hoặc phòng ban, trừ chính nó. Lỗi hiện ngay dưới ô, kể cả lỗi theo
 * trường backend trả về.
 */
export function DonViFormDialog({
  open,
  donVi,
  tatCa,
  saving,
  loiMayChu,
  onClose,
  onSubmit,
}: {
  open: boolean;
  donVi: DonVi | null;
  tatCa: DonVi[];
  saving: boolean;
  loiMayChu: unknown;
  onClose: () => void;
  onSubmit: (vao: GhiDonVi) => void;
}) {
  const { t } = useT();
  const [form, setForm] = useState<FormState>(RONG);
  const [loi, setLoi] = useState<Loi>({});

  useEffect(() => {
    if (!open) return;
    setLoi({});
    setForm(
      donVi
        ? {
            name: donVi.name,
            type: donVi.type,
            score: donVi.score != null ? String(donVi.score) : '',
            supervisor: donVi.supervisor ?? '',
            parentId: donVi.parent_id ?? '',
            functions: donVi.functions,
          }
        : RONG,
    );
  }, [open, donVi]);

  // Lỗi theo trường của backend (`#/parent_id`…) gắn vào đúng ô.
  useEffect(() => {
    const m = loiTheoTruong(loiMayChu);
    if (Object.keys(m).length) {
      setLoi({ name: m['/name'], score: m['/score'], parent_id: m['/parent_id'], functions: m['/functions'] });
    }
  }, [loiMayChu]);

  const loaiOptions = useMemo(
    () => LOAI_DON_VI.map((value) => ({ value, label: t(NHAN_LOAI_DON_VI[value]) })),
    [t],
  );
  const capTren = useMemo(
    () =>
      tatCa
        .filter((d) => d.type !== 'CA_NHAN' && d.id !== donVi?.id)
        .map((d) => ({ value: d.id, label: d.name })),
    [tatCa, donVi],
  );

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const luu = () => {
    const next: Loi = {};
    const name = form.name.trim();
    const functions = form.functions.trim();
    if (!name) next.name = t('vanBanDonVi.loiTenTrong');
    if (!functions) next.functions = t('vanBanDonVi.loiNhiemVuTrong');
    const score = form.score.trim() === '' ? null : Number(form.score);
    if (score != null && (Number.isNaN(score) || score < 0 || score > 10)) next.score = t('vanBanDonVi.loiDiemSai');
    setLoi(next);
    if (Object.keys(next).length) return;
    onSubmit({
      name,
      type: form.type,
      supervisor: form.type === 'CA_NHAN' ? null : form.supervisor.trim() || null,
      parent_id: form.parentId || null,
      functions,
      // Bỏ hẳn trường khi rỗng để server áp mặc định 8.0, như bản cũ
      ...(score != null ? { score: Math.round(score * 10) / 10 } : {}),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={520}
      destroyOnHidden
      title={t(donVi ? 'vanBanDonVi.dialogSua' : 'vanBanDonVi.dialogThem')}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" icon={<CheckOutlined />} loading={saving} onClick={luu}>
            {t(saving ? 'chung.dangLuu' : donVi ? 'vanBanChung.luuThayDoi' : 'vanBanChung.themMoi')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <LoiForm loi={loiMayChu} />
        <Field label={t('vanBanDonVi.fTen')} required error={loi.name}>
          {(p) => (
            <Input
              {...p}
              placeholder={t('vanBanDonVi.fTenGoiY')}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          )}
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('vanBanDonVi.fLoai')} required>
            {(p) => (
              <Select
                {...p}
                className="w-full"
                options={loaiOptions}
                value={form.type}
                onChange={(v) => set('type', v as LoaiDonVi)}
              />
            )}
          </Field>
          <Field label={t('vanBanDonVi.fDiem')} error={loi.score}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                max={10}
                step={0.1}
                value={form.score}
                onChange={(e) => set('score', e.target.value)}
              />
            )}
          </Field>
        </div>
        {form.type !== 'CA_NHAN' && (
          <Field label={t('vanBanDonVi.fPhuTrach')}>
            {(p) => (
              <Input
                {...p}
                placeholder={t('vanBanDonVi.fPhuTrachGoiY')}
                value={form.supervisor}
                onChange={(e) => set('supervisor', e.target.value)}
              />
            )}
          </Field>
        )}
        <Field label={t('vanBanDonVi.fTrucThuoc')} error={loi.parent_id}>
          {(p) => (
            <Select
              {...p}
              allowClear
              showSearch
              optionFilterProp="label"
              className="w-full"
              placeholder={t('vanBanDonVi.khongTrucThuoc')}
              options={capTren}
              value={form.parentId || undefined}
              onChange={(v) => set('parentId', (v as string | undefined) ?? '')}
            />
          )}
        </Field>
        <Field label={t('vanBanDonVi.fNhiemVu')} required error={loi.functions}>
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              placeholder={t('vanBanDonVi.fNhiemVuGoiY')}
              value={form.functions}
              onChange={(e) => set('functions', e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
